import { useQuery } from "@tanstack/react-query";

export const LOGIN_STATUS_QUERY_KEY = "login-status";

export class AuthStatusError extends Error {
  constructor(
    public readonly kind:
      | "server"
      | "network"
      | "timeout"
      | "invalid-response",
    public readonly status?: number,
  ) {
    super(`Authentication status failed: ${kind}`);
    this.name = "AuthStatusError";
  }
}

export function useLoginStatus() {
  const loginStatus = useQuery({
    queryKey: [LOGIN_STATUS_QUERY_KEY],
    queryFn: fetchLoginStatus,
    staleTime: Infinity,
    refetchOnWindowFocus: "always",
    retry: (failureCount, error) => {
      if (error instanceof AuthStatusError) {
        if (error.kind === "timeout" || error.kind === "invalid-response") {
          return false;
        }
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) =>
      Math.min(1000 * 2 ** attemptIndex, 5000),
  });
  return loginStatus;
}

export type LoginStatus = LoggedOut | LoginData;

export type LoginData = {
  state: "logged in";
  username: string;
  userId: number;
  is_staff: boolean;
  permissions: string[];
};

export type LoggedOut = {
  state: "logged out";
};

async function fetchLoginStatus({
  signal: querySignal,
}: {
  signal: AbortSignal;
}): Promise<LoginStatus> {
  const timeoutSignal = AbortSignal.timeout(15_000);
  const combinedSignal = AbortSignal.any([querySignal, timeoutSignal]);

  let response: Response;

  try {
    response = await fetch("/accounts/whoami", {
      signal: combinedSignal,
      redirect: "manual",
    });
  } catch (error) {
    if (querySignal.aborted && !timeoutSignal.aborted) {
      throw error;
    }

    if (timeoutSignal.aborted) {
      throw new AuthStatusError("timeout");
    }

    throw new AuthStatusError("network");
  }

  if (response.type === "opaqueredirect") {
    throw new AuthStatusError("invalid-response");
  }

  if (!response.ok) {
    throw new AuthStatusError("server", response.status);
  }

  const contentType = response.headers.get("content-type");
  if (!contentType || !contentType.includes("application/json")) {
    throw new AuthStatusError("invalid-response", response.status);
  }

  let data: Record<string, unknown>;
  try {
    data = await response.json();
  } catch {
    throw new AuthStatusError("invalid-response", response.status);
  }

  if (!data.userId && !data.username) {
    return { state: "logged out" };
  }

  return { ...data, state: "logged in" } as LoginData;
}
